import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import cn from 'classnames';
import find from 'lodash/find';
import moment from 'moment';
import 'moment/locale/ru';

import services from '../../services/services';
import { setMobileMenuEnabled, setMobileSidebarEnabled } from '../../actions/actions';
import { getRoomName, generateRoomURL } from '../../utils/utilsChat';
import {
  scrollToElement,
  withMomentLocale,
  isMobileSizeScreen,
  exposeComponentToGlobalScope,
} from '../../utils/utilsGlobal';

import { RoomSearch, RoomFilter, Avatar, RoomQueue } from '../../components';
import styles from './RoomList.scss';

const mapStateToProps = state => {
  return {
    currentCompany: state.currentCompany,
    currentOperator: state.currentOperator,
    currentRoom: state.currentRoom,
    currentRoomsByFilter: state.currentRoomsByFilter,
    currentRoomsBySearchTerm: state.currentRoomsBySearchTerm,
    currentRoomFilter: state.currentRoomFilter,
    currentCompanyOperators: state.currentCompanyOperators,
  };
};

const mapDispatchToProps = {
  setMobileMenuEnabled,
  setMobileSidebarEnabled,
};

@connect(mapStateToProps, mapDispatchToProps)
@withRouter
export class RoomList extends Component {

  state = {
    isLoading: false,
    isLoadingPrecedingRooms: false,
    latestMessageTimestamp: null,
    filteringType: 'filter', // also could be 'searchTerm'
    hasClickedRoomFromSearch: false,
  };

  scrollBlock = React.createRef();
  roomSearch = React.createRef();
  roomRefs = [];
  rerenderInterval = null;

  componentDidMount(){
    this.scrollToRoom(services.roomListSubscription.currentRoom.id);

    // Force rerender every minute to update "N minutes ago" labels
    this.rerenderInterval = setInterval(() => {
      this.forceUpdate();
    }, 60 * 1000);

    exposeComponentToGlobalScope(this);
  }

  componentWillUnmount(){
    clearInterval(this.rerenderInterval);
  };

  onRoomSearchChange = (searchTerm) => {
    this.setState({
      isLoading: true,
      hasClickedRoomFromSearch: false,
    });
    return services.roomListSubscription.setRoomsBySearchTerm(searchTerm).then(() => {
      this.setState({ isLoading: false });
    });
  };

  onRoomFilterChange = (roomFilter) => {
    this.setState({ isLoading: true });
    return services.roomListSubscription.setRoomsByFilter(roomFilter).then(() => {
      this.setState({ isLoading: false });
    });
  };

  loadNextRoomChunk = () => {
    const { isLoading, isLoadingPrecedingRooms, filteringType } = this.state;
    const scrollBlock = this.scrollBlock.current;
    const initialScrollTop = scrollBlock.scrollTop;

    const canFetchNextRoomChunk = !isLoading
      && !isLoadingPrecedingRooms
      && (
        (filteringType === 'filter' && !services.roomListSubscription.reachedEndOfRoomsByFilter) ||
        (filteringType === 'searchTerm' && !services.roomListSubscription.reachedEndOfRoomsBySearchTerm)
      );

    if (canFetchNextRoomChunk) {
      this.setState({ isLoadingPrecedingRooms: true });
      services.roomListSubscription.fetchNextChunk(filteringType).then(() => {
        this.setState({ isLoadingPrecedingRooms: false });
        scrollBlock.scrollTop = initialScrollTop;
      });
    }
  };

  onRoomsScroll = () => {
    const scrollBlock = this.scrollBlock.current;
    const maxScrollTop = scrollBlock.scrollHeight - scrollBlock.offsetHeight;
    if (scrollBlock.scrollTop >= maxScrollTop) {
      this.loadNextRoomChunk();
    }
  };

  scrollToRoom = (roomId) => {
    setTimeout(() => {
      scrollToElement(this.roomRefs[roomId]);
    }, 500);
  };

  onRoomClick = () => {
    const { setMobileMenuEnabled, setMobileSidebarEnabled } = this.props;
    const { filteringType } = this.state;
    if (filteringType === 'searchTerm') {
      this.setState({ hasClickedRoomFromSearch: true });
    }
    if (isMobileSizeScreen()) {
      this.scrollBlock.current.scrollTop = 0;
      setMobileMenuEnabled(false);
      setMobileSidebarEnabled(false);
    }
  };

  createRoomRef = (roomElement, room) => {
    this.roomRefs[room.id] = roomElement;
  };

  getTimestamp = (room) => {
    const time = +new Date(room.lastMessageAt || room.insertedAt);
    const now = +new Date();
    return withMomentLocale('ru', () => {
      return moment(Math.min(time, now)).fromNow();
    });
  };

  toggleFilteringType = (filteringType) => {
    this.setState({ filteringType });
    if (filteringType === 'filter') {
      services.roomListSubscription.setRoomsBySearchTerm('');
      this.roomSearch.current?.reset();
    }
    else if (filteringType === 'searchTerm') {
      this.roomSearch.current?.focusInput();
    }
  };

  render(){
    const {
      isLoading,
      filteringType,
      hasClickedRoomFromSearch,
    } = this.state;

    const {
      className,
      currentCompany,
      currentRoom,
      currentOperator,
      currentRoomFilter,
      currentCompanyOperators,
      currentRoomsByFilter,
      currentRoomsBySearchTerm,
    } = this.props;

    const noRoomsByFilterFound = currentRoomsByFilter.length >= 1 && currentRoomsByFilter[0].id === 'default' && !isLoading;
    const selectedOperator = find(currentCompanyOperators, { id: currentRoomFilter });
    const highlightSelectedRoom = filteringType === 'filter' || (filteringType === 'searchTerm' && hasClickedRoomFromSearch);
    const rooms = filteringType === 'searchTerm' ? currentRoomsBySearchTerm : currentRoomsByFilter;

    return (
      <div className={cn({
        [className]: true,
        [styles.container]: true,
        [styles.containerSearch]: filteringType === 'searchTerm',
      })}
        onScroll={this.onRoomsScroll}
        ref={this.scrollBlock}>

        <i className={cn(styles.searchOpenIcon, 'icon-search')}
          onClick={() => this.toggleFilteringType('searchTerm')}/>

        <i className={cn(styles.searchCloseIcon, 'icon-close-medium')}
          onClick={() => this.toggleFilteringType('filter')}/>

        <div className={styles.roomSearch}>
          <RoomSearch ref={this.roomSearch} onChange={searchTerm => this.onRoomSearchChange(searchTerm)}/>
        </div>

        <RoomFilter className={styles.roomFilter}
          onChange={operatorId => this.onRoomFilterChange(operatorId)}/>

        {currentOperator.isManager && (
          <Fragment>
            <RoomQueue className={styles.roomQueue}/>
          </Fragment>
        )}

        <ul className={cn({
          [styles.list]: true,
          [styles.listLoading]: isLoading,
        })}>
          {!isLoading && rooms.map(room => (
            <li className={cn({
                [styles.room]: true,
                [styles.roomActive]: room.id === currentRoom.id && highlightSelectedRoom,
                [styles.roomDefault]: room.id === 'default',
                [styles.roomUnread]: room.isCaseOpen,
              })}
              onClick={this.onRoomClick}
              ref={element => this.createRoomRef(element, room)}
              key={room.id}>

              <Link className={styles.link} to={generateRoomURL(currentCompany.id, room.id)}>
                <Avatar room={room} overrideClassNames={{
                  avatar: styles.avatar,
                  avatarDefaultColor: styles.avatarDefaultColor,
                }}/>
                <h3 className={styles.title}>
                  {getRoomName(room)}
                </h3>
                <div className={styles.lastMessageAt}>
                  {this.getTimestamp(room)}
                </div>
                {Boolean(room.unreadMessagesCount) && (
                  <div className={styles.unreadCount}>{room.unreadMessagesCount}</div>
                )}
              </Link>
            </li>
          ))}
        </ul>

        {noRoomsByFilterFound && filteringType !== 'searchTerm' && (
          <div className={styles.hasNoRooms}>
            {currentOperator.id === currentRoomFilter && (
              <Fragment>Нет чатов, <br/>назначенных на вас</Fragment>
            )}
            {currentOperator.id !== currentRoomFilter && selectedOperator && (
              <Fragment>Нет чатов, за которые отвечает {selectedOperator.firstName}</Fragment>
            )}
            {currentRoomFilter === 'all-rooms' && (
              <Fragment>Нет доступных чатов</Fragment>
            )}
            {currentRoomFilter === 'new-rooms' && (
              <Fragment>Нет новых чатов</Fragment>
            )}
          </div>
        )}

      </div>
    );
  }
}
